// src/hooks/useLeave.js
import { useState, useCallback, useRef } from 'react'; // ✅ Add useRef import
import * as leaveService from '../services/leaveService';

export const useLeave = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestCount = useRef(0); // ✅ Track requests to prevent loops

  const clearError = useCallback(() => setError(null), []);

  // ============ Employee Methods ============

  const getMyBalance = useCallback(async (year) => {
    setLoading(true);
    try {
      const response = await leaveService.getMyBalance(year);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getMySummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await leaveService.getMySummary();
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getMyRequests = useCallback(async (params = {}) => {
    // ✅ Prevent concurrent identical requests
    requestCount.current += 1;
    const currentRequest = requestCount.current;
    
    setLoading(true);
    try {
      const response = await leaveService.getMyRequests(params);
      // Only update if this is the latest request
      if (currentRequest === requestCount.current) {
        setError(null);
        return response;
      }
      return null;
    } catch (err) {
      if (currentRequest === requestCount.current) {
        setError(err.message);
      }
      throw err;
    } finally {
      if (currentRequest === requestCount.current) {
        setLoading(false);
      }
    }
  }, []);

  const applyLeave = useCallback(async (data) => {
    setLoading(true);
    try {
      const response = await leaveService.applyLeave(data);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateLeave = useCallback(async (id, data) => {
    setLoading(true);
    try {
      const response = await leaveService.updateLeaveRequest(id, data);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const submitDraft = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await leaveService.submitLeaveRequest(id);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const cancelLeave = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await leaveService.cancelLeaveRequest(id);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const withdrawLeave = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await leaveService.withdrawLeaveRequest(id);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteLeave = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await leaveService.deleteLeaveRequest(id);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============ HR Methods ============

  const getPendingRequests = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await leaveService.getPendingRequests(params);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getApprovedRequests = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await leaveService.getApprovedRequests(params);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getRejectedRequests = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await leaveService.getRejectedRequests(params);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getHrDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await leaveService.getHrDashboard();
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const approveLeave = useCallback(async (id, data) => {
    setLoading(true);
    try {
      const response = await leaveService.approveLeave(id, data);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const rejectLeave = useCallback(async (id, data) => {
    setLoading(true);
    try {
      const response = await leaveService.rejectLeave(id, data);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reopenLeave = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await leaveService.reopenLeave(id);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getEmployeeLeaves = useCallback(async (employeeId, params = {}) => {
    setLoading(true);
    try {
      const response = await leaveService.getEmployeeLeaves(employeeId, params);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const adjustBalance = useCallback(async (employeeId, data) => {
    setLoading(true);
    try {
      const response = await leaveService.adjustBalance(employeeId, data);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const bulkApprove = useCallback(async (data) => {
    setLoading(true);
    try {
      const response = await leaveService.bulkApprove(data);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const bulkReject = useCallback(async (data) => {
    setLoading(true);
    try {
      const response = await leaveService.bulkReject(data);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getBalanceHistory = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await leaveService.getBalanceHistory(params);
      setError(null);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    loading,
    error,
    clearError,

    // Employee methods
    getMyBalance,
    getMySummary,
    getMyRequests,
    applyLeave,
    updateLeave,
    submitDraft,
    cancelLeave,
    withdrawLeave,
    deleteLeave,

    // HR methods
    getPendingRequests,
    getApprovedRequests,
    getRejectedRequests,
    getHrDashboard,
    approveLeave,
    rejectLeave,
    reopenLeave,
    getEmployeeLeaves,
    adjustBalance,
    bulkApprove,
    bulkReject,
    getBalanceHistory,
  };
};